import { useState } from "react";
import { Mail, Phone, MapPin, Instagram, Facebook, Twitter, ArrowUpRight, Download } from "lucide-react";
import { toast } from "sonner";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF, CONTACT_ADDRESS } from "@/data";
import { Reveal, MaskLine } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", interest: "Franchise enquiry", message: "" };

export const ContactSection = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email) || !form.message.trim()) {
      toast.error("Please add your name, a valid email and a message.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("bad response");
      toast.success("Merci! Your enquiry is in — we'll reply within two days.");
      setForm(EMPTY);
      setSent(true);
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-36 bg-[#F8F3EA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-12">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="eyebrow mb-5">Say Bonjour</p>
          </Reveal>
          <h2 className="font-display font-medium leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            <MaskLine>Bring French Pie</MaskLine>
            <MaskLine delay={0.12}>
              to <span className="italic text-[#C85A32]">your city.</span>
            </MaskLine>
          </h2>
          <Reveal>
            <p className="mt-6 text-base leading-relaxed text-[#1C130E]/75 max-w-md">
              Whether you're dreaming of your own outlet or planning next week's office breakfast — write to us. A
              human (flour-dusted) replies to every message.
            </p>
          </Reveal>
          <Reveal>
            <div className="mt-10 space-y-3 text-sm">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-[#1C130E]/75 hover:text-[#C85A32] transition-colors w-fit"
              >
                <Mail size={16} className="text-[#C85A32]" />
                {CONTACT_EMAIL}
              </a>
              <a
                href={CONTACT_PHONE_HREF}
                data-testid="contact-phone-link"
                className="flex items-center gap-3 text-[#1C130E]/75 hover:text-[#C85A32] transition-colors w-fit"
              >
                <Phone size={16} className="text-[#C85A32]" />
                {CONTACT_PHONE}
              </a>
              <p data-testid="contact-address-line" className="flex items-start gap-3 text-[#1C130E]/75 w-fit">
                <MapPin size={16} className="text-[#C85A32] shrink-0 mt-0.5" />
                {CONTACT_ADDRESS}
              </p>
            </div>
            <div className="mt-8 flex items-center gap-3">
              {[Instagram, Facebook, Twitter].map((Icon, i) => (
                <a
                  key={i}
                  href="#top"
                  data-testid={`contact-social-${i}`}
                  onClick={(e) => e.preventDefault()}
                  aria-label="Social link"
                  className="grid place-items-center w-10 h-10 rounded-full border border-[rgba(28,19,14,0.2)] hover:bg-[#C85A32] hover:border-[#C85A32] hover:text-[#FDFBF7] transition-colors"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-6">
          <form
            onSubmit={submit}
            data-testid="franchise-enquiry-form"
            className="rounded-3xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7] p-8 lg:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-7">
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                  Full name *
                </span>
                <input
                  className="field mt-1"
                  data-testid="form-name-input"
                  placeholder="Jeanne Dupont"
                  required
                  value={form.name}
                  onChange={set("name")}
                />
              </label>
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">Email *</span>
                <input
                  className="field mt-1"
                  data-testid="form-email-input"
                  placeholder="you@example.com"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                />
              </label>
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">Phone</span>
                <input
                  className="field mt-1"
                  data-testid="form-phone-input"
                  placeholder="+91 …"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </label>
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                  I'm interested in
                </span>
                <select
                  className="field mt-1"
                  data-testid="form-interest-select"
                  value={form.interest}
                  onChange={set("interest")}
                >
                  {["Franchise enquiry", "Visit & catering", "Wholesale", "Something else"].map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                  Message *
                </span>
                <textarea
                  rows="4"
                  className="field mt-1 resize-none"
                  data-testid="form-message-input"
                  placeholder="Tell us about your city, your space, your dream…"
                  required
                  value={form.message}
                  onChange={set("message")}
                />
              </label>
            </div>
            <button
              type="submit"
              data-testid="form-submit-btn"
              disabled={busy}
              className="group mt-9 inline-flex items-center justify-center gap-2 rounded-full bg-[#1C130E] text-[#FDFBF7] px-8 py-4 text-sm font-semibold hover:bg-[#C85A32] disabled:opacity-60 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {busy ? "Sending…" : "Send enquiry"}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </button>
            {sent && (
              <div
                data-testid="form-success-panel"
                className="mt-6 rounded-2xl border border-[#C85A32]/30 bg-[#C85A32]/5 p-5"
              >
                <p className="text-sm font-semibold">Merci! Your enquiry is in — we'll reply within two days.</p>
                <p className="text-sm text-[#1C130E]/65 mt-1">Want a head start? Grab the franchise deck.</p>
                <a
                  href="/french-pie-franchise-deck.pdf"
                  download
                  data-testid="franchise-deck-download-btn"
                  className="group mt-4 inline-flex items-center gap-2 rounded-full bg-[#C85A32] text-[#FDFBF7] px-6 py-3 text-sm font-semibold hover:bg-[#B04B26] transition-colors"
                >
                  <Download size={16} />
                  Download the Franchise Deck (PDF)
                </a>
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};
